import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { database, storage } from 'helpers/firebase';
import { getFileIcon } from 'helpers/functions';
import Paragraph from 'components/atoms/Paragraph';
import Heading from 'components/atoms/Heading';
import { fadeInDown } from 'helpers/animations';
import Collapse from '@kunukn/react-collapse';
import { ReactComponent as ArrowLeft } from 'assets/icons/arrow_left.svg';
import { ReactComponent as Edit } from 'assets/icons/edit.svg';
import { ReactComponent as Remove } from 'assets/icons/remove.svg';
import { ReactComponent as Check } from 'assets/icons/check.svg';
import { ReactComponent as Minus } from 'assets/icons/minus.svg';
import { options } from 'helpers/constants';

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.gray5};
  border-radius: 10px;
  margin-top: 15px;
  animation: ${fadeInDown} ${({ theme }) => theme.fadeInDown} ${({ delay }) => delay}s;
`;

const StyledHeading = styled(Heading)`
  font-size: 21px;
`;

const ButtonIcon = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background-color: transparent;
  cursor: pointer;
  fill: ${({ theme }) => theme.text};
  height: 48px;
  width: 48px;
  border-radius: 50%;
  outline: none;

  :hover {
    background-color: ${({ theme }) => theme.gray4};
  }

  svg {
    height: 32px;
    width: 32px;
  }
`;

const Header = styled.label`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  border: none;
  background-color: transparent;
  height: 89px;
  width: 100%;
  padding: 0 25px;
  outline: none;

  ${ButtonIcon} {
    opacity: 0;
    display: none;
  }

  :hover ${ButtonIcon} {
    opacity: 1;
    display: initial;
  }
`;

const StyledCollapse = styled(Collapse)`
  transition: height 0.28s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Content = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;
  min-height: 89px;
  width: 100%;
  padding: 15px 25px;
  gap: 25px;
`;

const ButtonMark = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 55px;
  width: 55px;
  background-color: ${({ theme, isOpen }) => (isOpen ? theme.blue : theme.gray4)};
  border-radius: 8px;
  margin-right: 13px;
  transition: background-color 0.1s ease-in-out;
  cursor: pointer;
  outline: none;
  border: none;
`;

const StyledIcon = styled(ArrowLeft)`
  transform: rotate(${({ isopen }) => (isopen ? '270deg' : '90deg')});
  fill: ${({ theme, isopen }) => (isopen ? theme.text : theme.textSecondary)};
  height: 55px;
  width: 55px;
  transition: transform 0.2s ease-in-out, color 0.1s ease-in-out;
`;

const Preview = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  min-width: 300px;

  ${({ hide }) =>
    hide
      ? css`
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.15s ease-in-out, visibility 0s linear 0.15s;
        `
      : css`
          opacity: 1;
          visibility: visible;
          transition: opacity 0.15s ease-in-out;
        `}
`;

const PreviewTitle = styled(Paragraph)`
  font-size: 21px;
  text-align: left;
  font-weight: 500;
`;

const PreviewDescription = styled(Paragraph)`
  color: ${({ theme }) => theme.textSecondary};
  text-align: left;
  text-overflow: hidden;
  max-height: 30px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${({ full }) => (full ? 'auto' : 0)};
  margin-left: ${({ smallGap }) => (smallGap ? '15px' : 0)};
  gap: ${({ smallGap }) => (smallGap ? '10px' : '25px')};
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.blue};
  border: none;
  border-radius: 10px;
  padding: 4px 8px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  cursor: pointer;
  outline: none;
  margin-left: 15px;
  transition: background-color 0.05s ease-in-out;

  :hover:not(:disabled) {
    background-color: ${({ theme }) => theme.blueHover};
  }

  :disabled {
    opacity: 0.38;
    cursor: default;
  }
`;

const PreviewFiles = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const File = styled.button`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  background-color: ${({ theme }) => theme.gray4};
  padding: 4px 8px;
  border: none;
  border-radius: 5px;
  margin: ${({ column }) => (column ? '10px 0 0' : '0 10px 0 0')};
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  pointer-events: ${({ noInteraction }) => (noInteraction ? 'none' : 'auto')};
  outline: none;

  :hover:not(:disabled) {
    background-color: ${({ theme }) => theme.gray3};
    cursor: pointer;
  }

  :disabled {
    opacity: 0.38;
  }
`;

const Icon = styled.div`
  height: 24px;
  width: 24px;
  margin-right: 10px;

  img {
    height: 24px;
    width: 24px;
  }

  svg {
    height: 24px;
    width: 24px;
  }
`;

const StyledParagraph = styled(Paragraph)`
  color: ${({ theme }) => theme.textSecondary};
`;

const Badge = styled(StyledParagraph)`
  padding: 4px 8px;
  border: 1px solid ${({ theme, color }) => theme[color]};
  color: ${({ theme, color }) => theme[color]};
  background-color: transparent;
  border-radius: 8px;
`;

const getBadgeColor = (type) => {
  switch (type) {
    case 'exercise':
      return 'red';
    case 'lecture':
      return 'green';
    case 'lab':
    default:
      return 'blue';
  }
};

function Task({
  name,
  description,
  id,
  delay,
  files,
  isAdmin,
  handleEdit,
  handleOpenFileModal,
  person,
  type,
  isChecked,
  isVerified,
  isMobile,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleRemove = () => {
    if (isAdmin) {
      const ref = database.ref(`tasks/${id}`);

      ref.remove();
    }
  };

  const handleCheck = (id) => {
    const ref = database.ref(`/tasks/${id}`);
    ref.update({
      isChecked: !isChecked,
    });
  };

  const handleDownload = (key, n) => {
    setIsDisabled(true);

    storage
      .ref(key)
      .getDownloadURL()
      .then((url) => {
        window
          .fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const blobURL = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobURL;
            a.style = 'display: none';

            if (n && n.length) a.download = n;
            document.body.appendChild(a);
            a.click();

            setIsDisabled(false);
          });
      });
  };

  return (
    <Wrapper delay={delay * 0.02}>
      <Header>
        <ButtonMark
          type="button"
          onClick={() => setIsOpen((prevState) => !prevState)}
          isOpen={isOpen}
        >
          <StyledIcon isopen={isOpen ? 1 : 0} />
        </ButtonMark>
        <Row full>
          <Preview>
            <StyledHeading>{name}</StyledHeading>
          </Preview>
          {!isMobile ? (
            <>
              {description.length ? (
                <Preview hide={isOpen}>
                  <PreviewTitle>Opis</PreviewTitle>
                  <PreviewDescription>{description}</PreviewDescription>
                </Preview>
              ) : null}
              {files.length ? (
                <Preview hide={isOpen}>
                  <PreviewTitle>Pliki</PreviewTitle>
                  <PreviewFiles>
                    <File
                      type="button"
                      onClick={() => handleDownload(files[0].key, files[0].name)}
                      disabled={isDisabled}
                    >
                      <Icon>{getFileIcon(files[0].type)}</Icon>
                      <Paragraph>{files[0].name}</Paragraph>
                    </File>
                    {files.length > 1 ? (
                      <File type="button" noInteraction>
                        <Paragraph>+{files.length - 1}</Paragraph>
                      </File>
                    ) : null}
                  </PreviewFiles>
                </Preview>
              ) : null}
            </>
          ) : null}
        </Row>
        {(type || person) && !isMobile ? (
          <Row>
            {person && <Badge>{person}</Badge>}
            {type && (
              <Badge color={getBadgeColor(type)}>
                {options.find(({ value }) => value === type).label}
              </Badge>
            )}
          </Row>
        ) : null}
        <Row smallGap>
          {(isVerified || isAdmin) && !isMobile ? (
            <>
              <ButtonIcon type="button" onClick={() => handleCheck(id)}>
                {isChecked ? <Minus /> : <Check />}
              </ButtonIcon>
              <ButtonIcon type="button" onClick={() => handleEdit(id)}>
                <Edit />
              </ButtonIcon>
              {isAdmin ? (
                <ButtonIcon type="button" onDoubleClick={handleRemove}>
                  <Remove fill="rgb(255, 69, 58)" />
                </ButtonIcon>
              ) : null}
            </>
          ) : null}
        </Row>
      </Header>
      <StyledCollapse isOpen={isOpen}>
        <Content>
          <Preview hide={!isOpen}>
            <PreviewTitle>Opis</PreviewTitle>
            <StyledParagraph as="pre" style={{ maxWidth: "50%" }}>{description}</StyledParagraph>
          </Preview>
          <Preview hide={!isOpen}>
            <Row>
              <PreviewTitle>Pliki</PreviewTitle>
              <Button type="button" onClick={() => handleOpenFileModal(id)}>
                <Paragraph>Wybierz pliki</Paragraph>
              </Button>
            </Row>
            {files.map(({ key, name: n, type: fileType }) => (
              <File
                type="button"
                column
                onClick={() => handleDownload(key, n)}
                disabled={isDisabled}
              >
                <Icon>{getFileIcon(fileType)}</Icon>
                <Paragraph>{n}</Paragraph>
              </File>
            ))}
          </Preview>
          {(type || person) && isMobile ? (
            <Row>
              {person && <Badge>{person}</Badge>}
              {type && (
                <Badge color={getBadgeColor(type)}>
                  {options.find(({ value }) => value === type).label}
                </Badge>
              )}
            </Row>
          ) : null}
          {(isVerified || isAdmin) && isMobile ? (
            <Row>
              <ButtonIcon type="button" onClick={() => handleCheck(id)}>
                {isChecked ? <Minus /> : <Check />}
              </ButtonIcon>
              <ButtonIcon type="button" onClick={() => handleEdit(id)}>
                <Edit />
              </ButtonIcon>
              {isAdmin ? (
                <ButtonIcon type="button" onDoubleClick={handleRemove}>
                  <Remove fill="rgb(255, 69, 58)" />
                </ButtonIcon>
              ) : null}
            </Row>
          ) : null}
        </Content>
      </StyledCollapse>
    </Wrapper>
  );
}

Task.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleOpenFileModal: PropTypes.func.isRequired,
  description: PropTypes.string,
  person: PropTypes.string,
  type: PropTypes.string,
  delay: PropTypes.number,
  isAdmin: PropTypes.bool,
  isChecked: PropTypes.bool,
  isVerified: PropTypes.bool,
  isMobile: PropTypes.bool,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      folder: PropTypes.string,
      key: PropTypes.string,
      lastModified: PropTypes.number,
      uploaded: PropTypes.number,
      name: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
};

Task.defaultProps = {
  delay: 1,
  description: '',
  files: [],
  isAdmin: false,
  person: '',
  type: '',
  isChecked: false,
  isVerified: false,
  isMobile: false,
};

export default Task;
